import { computed, signal } from '@preact/signals-react';
import type {
  ConversationMessage,
  ConversationMessageAttachmentImageSize,
  ConversationMessageContent,
} from '@/embedded/chat/types/conversation-message';
import { updateConversationMessagesAttachmentImageSize } from '@/embedded/chat/types/conversation-message';
import type {
  WebAgentConversationMessage,
  WebAgentConversationMessageDraft,
  WebAgentConversationParticipant,
  WebAgentMessageContent,
} from '@/website/lib/chat-api';
import type { ConversationLiveDataset } from './conversation-live-dataset';
import { normalizeText } from './conversation-utils';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function metadataAppRequest(metadata: unknown): Record<string, unknown> | null {
  if (!isRecord(metadata) || !isRecord(metadata.app_request)) return null;
  return metadata.app_request;
}

function metadataString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function metadataStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const values = value
    .map(item => metadataString(item))
    .filter((item): item is string => !!item);
  return values.length > 0 ? values : undefined;
}

function metadataHostAccess(
  value: unknown
): ConversationMessageContent['hostAccess'] | undefined {
  if (!isRecord(value)) return undefined;
  const hostAccess = {
    environmentId:
      metadataString(value.environment_id) ??
      metadataString(value.environmentId) ??
      metadataString(value.environment),
    environmentLabel:
      metadataString(value.environment_label) ??
      metadataString(value.environmentLabel),
    description: metadataString(value.description),
    status: metadataString(value.status),
    approved: typeof value.approved === 'boolean' ? value.approved : undefined,
  };
  return Object.values(hostAccess).some(item => item !== undefined)
    ? hostAccess
    : undefined;
}

function metadataComputerUseStart(
  value: unknown
): ConversationMessageContent['computerUseStart'] | undefined {
  if (!isRecord(value)) return undefined;
  const computerUseStart = {
    environmentId:
      metadataString(value.environment_id) ??
      metadataString(value.environmentId) ??
      metadataString(value.environment),
    environmentLabel:
      metadataString(value.environment_label) ??
      metadataString(value.environmentLabel),
    availableModes:
      metadataStringArray(value.available_modes) ??
      metadataStringArray(value.availableModes),
    apps: metadataStringArray(value.apps),
    status: metadataString(value.status),
    approved: typeof value.approved === 'boolean' ? value.approved : undefined,
    mode: metadataString(value.mode),
  };
  return Object.values(computerUseStart).some(item => item !== undefined)
    ? computerUseStart
    : undefined;
}

function metadataClientRequestId(metadata: unknown): string | undefined {
  if (!isRecord(metadata)) return undefined;
  const client = isRecord(metadata.client) ? metadata.client : null;
  return (
    metadataString(metadata.client_request_id) ??
    metadataString(metadata.clientRequestId) ??
    metadataString(client?.request_id) ??
    metadataString(client?.client_request_id) ??
    metadataString(client?.clientRequestId)
  );
}

type ConversationMessageRuntimeIdentity = {
  participantId?: string;
  agentId?: string;
  sessionId?: string;
};

const runtimeIdentitySymbol: unique symbol = Symbol(
  'conversationMessageRuntimeIdentity'
);

type RuntimeIdentifiedConversationMessage = ConversationMessage & {
  [runtimeIdentitySymbol]?: ConversationMessageRuntimeIdentity;
};

function runtimeIdentityValue(value: string | null | undefined) {
  return normalizeText(value) ?? undefined;
}

function runtimeIdentityFromConversationMessage(
  message: WebAgentConversationMessage | WebAgentConversationMessageDraft
): ConversationMessageRuntimeIdentity | undefined {
  const identity = {
    participantId: runtimeIdentityValue(message.participantId),
    agentId: runtimeIdentityValue(message.agentId),
    sessionId: runtimeIdentityValue(message.sessionId),
  };
  return identity.participantId || identity.agentId || identity.sessionId
    ? identity
    : undefined;
}

function withRuntimeIdentity<T extends ConversationMessage>(
  message: T,
  identity: ConversationMessageRuntimeIdentity | undefined
): T {
  if (identity) {
    (message as RuntimeIdentifiedConversationMessage)[runtimeIdentitySymbol] =
      identity;
  }
  return message;
}

function messageRuntimeIdentity(
  message: ConversationMessage
): ConversationMessageRuntimeIdentity | undefined {
  return (message as RuntimeIdentifiedConversationMessage)[
    runtimeIdentitySymbol
  ];
}

function messageClientRequestId(
  message: ConversationMessage
): string | undefined {
  return metadataClientRequestId(message.metadata);
}

function appRequestStatusText(messageKind: string, metadata: unknown) {
  if (messageKind !== 'app_request') {
    return null;
  }
  const appRequest = metadataAppRequest(metadata);
  if (!appRequest) return null;

  const requestType = metadataString(appRequest.type);
  const status = metadataString(appRequest.status)?.toLowerCase();
  const reason = metadataString(appRequest.reason);
  const error = metadataString(appRequest.error);

  if (requestType === 'location') {
    if (status === 'pending' || !status) {
      return reason ? `Location requested: ${reason}` : 'Location requested';
    }
    if (status === 'success' || status === 'completed') {
      return 'Current location was shared.';
    }
    if (status === 'error' || status === 'failed') {
      return error
        ? `Current location unavailable: ${error}`
        : 'Current location unavailable.';
    }
  }

  if (requestType === 'oauth_authorization') {
    const provider = metadataString(appRequest.provider) ?? 'integration';
    const alias = metadataString(appRequest.alias);
    const integration = alias ? `${provider}/${alias}` : provider;
    if (status === 'pending' || !status) {
      return reason
        ? `Authorization requested for ${integration}: ${reason}`
        : `Authorization requested for ${integration}.`;
    }
    if (status === 'success' || status === 'completed') {
      return `Authorization completed for ${integration}.`;
    }
    if (status === 'error' || status === 'failed' || status === 'expired') {
      return error ? `Authorization failed: ${error}` : 'Authorization failed.';
    }
  }

  if (requestType === 'interactive_login') {
    if (status === 'pending' || !status) {
      return reason ? `Login requested: ${reason}` : 'Login requested';
    }
    if (status === 'success' || status === 'completed') {
      return 'Login completed.';
    }
    if (status === 'error' || status === 'failed' || status === 'expired') {
      return error ? `Login failed: ${error}` : 'Login failed.';
    }
  }

  if (requestType === 'host_access') {
    const hostAccess = isRecord(appRequest.host_access)
      ? appRequest.host_access
      : null;
    const description =
      metadataString(hostAccess?.description) ??
      metadataString(appRequest.description) ??
      reason;
    if (status === 'pending' || !status) {
      return description
        ? `Permission requested: ${description}`
        : 'Permission requested.';
    }
    if (
      status === 'approved' ||
      status === 'success' ||
      status === 'completed'
    ) {
      return 'Permission approved.';
    }
    if (status === 'denied') {
      return 'Permission denied.';
    }
    if (status === 'error' || status === 'failed' || status === 'expired') {
      return error ? `Permission failed: ${error}` : 'Permission failed.';
    }
  }

  if (requestType === 'computer_use_start') {
    const computerUseStart = isRecord(appRequest.computer_use_start)
      ? appRequest.computer_use_start
      : null;
    const environmentLabel =
      metadataString(computerUseStart?.environment_label) ??
      metadataString(appRequest.environment_label);
    if (status === 'pending' || !status) {
      return environmentLabel
        ? `ComputerUse start requested for ${environmentLabel}.`
        : 'ComputerUse start requested.';
    }
    if (
      status === 'approved' ||
      status === 'success' ||
      status === 'completed'
    ) {
      const mode = metadataString(computerUseStart?.mode);
      return mode
        ? `ComputerUse start approved: ${mode}.`
        : 'ComputerUse start approved.';
    }
    if (status === 'denied') {
      return 'ComputerUse start denied.';
    }
    if (status === 'error' || status === 'failed' || status === 'expired') {
      return error
        ? `ComputerUse start failed: ${error}`
        : 'ComputerUse start failed.';
    }
  }

  if (status === 'pending' || !status) {
    return 'App request pending.';
  }
  if (status === 'success' || status === 'completed') {
    return 'App request completed.';
  }
  if (status === 'error' || status === 'failed') {
    return error ? `App request failed: ${error}` : 'App request failed.';
  }
  return 'App request updated.';
}

export function appRequestContentFromMessageParts(params: {
  kind?: string | null;
  metadata?: unknown;
}): ConversationMessageContent[] | null {
  const text = appRequestStatusText(params.kind ?? '', params.metadata);
  if (!text) return null;
  const appRequest = metadataAppRequest(params.metadata);
  return [
    {
      type: 'app_request',
      text,
      requestId: metadataString(appRequest?.request_id),
      requestType: metadataString(appRequest?.type),
      status: metadataString(appRequest?.status)?.toLowerCase(),
      triggerClientInstanceId: metadataString(
        appRequest?.trigger_client_instance_id
      ),
      authorizationURL: metadataString(appRequest?.authorization_url),
      loginURL: metadataString(appRequest?.login_url),
      provider: metadataString(appRequest?.provider),
      alias: metadataString(appRequest?.alias),
      reason: metadataString(appRequest?.reason),
      startURL: metadataString(appRequest?.start_url),
      description: metadataString(appRequest?.description),
      environmentId:
        metadataString(appRequest?.environment_id) ??
        metadataString(appRequest?.environment),
      environmentLabel: metadataString(appRequest?.environment_label),
      hostAccess: metadataHostAccess(appRequest?.host_access),
      computerUseStart: metadataComputerUseStart(
        appRequest?.computer_use_start
      ),
    },
  ];
}

function conversationMessageRole(
  message: WebAgentConversationMessage
): ConversationMessage['role'] {
  const roleLabel = normalizeText(message.roleLabel)?.toLowerCase();
  const actorType = normalizeText(message.actorType)?.toLowerCase();
  if (actorType === 'user') {
    return 'user';
  }
  if (actorType === 'system') return 'system';
  if (actorType === 'tool') return 'tool';
  if (!actorType && roleLabel === 'user') return 'user';
  if (!actorType && roleLabel === 'system') return 'system';
  if (!actorType && roleLabel === 'tool') return 'tool';
  return 'assistant';
}

function contentString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function contentNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function contentBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeContentFileRef(
  value: unknown
): ConversationMessageContent['fileRef'] | undefined {
  if (!isRecord(value)) return undefined;
  const refId = contentString(value.refId) ?? contentString(value.ref_id);
  const path = contentString(value.path)?.trim();
  const displayURL =
    contentString(value.displayURL) ??
    contentString(value.displayUrl) ??
    contentString(value.display_url);
  const downloadURL =
    contentString(value.downloadURL) ??
    contentString(value.downloadUrl) ??
    contentString(value.download_url);
  const thumbnailURL =
    contentString(value.thumbnailURL) ??
    contentString(value.thumbnailUrl) ??
    contentString(value.thumbnail_url);
  const publicShareURL =
    contentString(value.publicShareURL) ??
    contentString(value.publicShareUrl) ??
    contentString(value.public_share_url);
  if (!path && !displayURL && !downloadURL && !publicShareURL && !refId) {
    return undefined;
  }
  const ref: NonNullable<ConversationMessageContent['fileRef']> = {
    refId,
    kind: contentString(value.kind),
    source: contentString(value.source),
    path,
    name: contentString(value.name),
    mimeType: contentString(value.mimeType) ?? contentString(value.mime_type),
    sizeBytes:
      contentNumber(value.sizeBytes) ?? contentNumber(value.size_bytes),
    contentHash:
      contentString(value.contentHash) ?? contentString(value.content_hash),
    displayURL,
    downloadURL,
    thumbnailURL,
    publicShareURL,
    authRequired:
      contentBoolean(value.authRequired) ?? contentBoolean(value.auth_required),
    authScope:
      contentString(value.authScope) ?? contentString(value.auth_scope),
    environmentId:
      contentString(value.environmentId) ?? contentString(value.environment_id),
    sourceSessionId:
      contentString(value.sourceSessionId) ??
      contentString(value.source_session_id),
    agentId: contentString(value.agentId) ?? contentString(value.agent_id),
    signature: contentString(value.signature),
    expiresAt:
      contentNumber(value.expiresAt) ?? contentNumber(value.expires_at),
  };
  return ref;
}

function normalizeContentFileRefs(
  value: unknown
): ConversationMessageContent['fileRefs'] | undefined {
  if (!Array.isArray(value)) return undefined;
  const refs = value.flatMap(ref => {
    const normalized = normalizeContentFileRef(ref);
    return normalized ? [normalized] : [];
  });
  return refs.length > 0 ? refs : undefined;
}

function selectPrimaryContentFileRef(params: {
  fileRef?: ConversationMessageContent['fileRef'];
  fileRefs?: ConversationMessageContent['fileRefs'];
}) {
  if (params.fileRef) return params.fileRef;
  return params.fileRefs?.[0];
}

function normalizeContentQuoteRef(
  value: unknown
): ConversationMessageContent['quoteRef'] | undefined {
  if (!isRecord(value)) return undefined;
  const sourceMessageId =
    contentString(value.sourceMessageId) ??
    contentString(value.source_message_id);
  const startOffset =
    contentNumber(value.startOffset) ?? contentNumber(value.start_offset);
  const endOffset =
    contentNumber(value.endOffset) ?? contentNumber(value.end_offset);
  if (
    sourceMessageId === undefined ||
    startOffset === undefined ||
    endOffset === undefined
  ) {
    return undefined;
  }
  return {
    sourceMessageId,
    startOffset,
    endOffset,
  };
}

function normalizeConversationContentBlock(
  block: unknown
): ConversationMessageContent | null {
  if (!isRecord(block)) return null;
  const type = contentString(block.type);
  if (!type) return null;

  const fileRef = normalizeContentFileRef(
    'fileRef' in block
      ? block.fileRef
      : 'file_ref' in block
        ? block.file_ref
        : undefined
  );
  const fileRefs = normalizeContentFileRefs(
    'fileRefs' in block
      ? block.fileRefs
      : 'file_refs' in block
        ? block.file_refs
        : undefined
  );
  const primaryFileRef = selectPrimaryContentFileRef({ fileRef, fileRefs });
  const content: ConversationMessageContent = { type };

  const text = contentString(block.text);
  if (text !== undefined) content.text = text;

  const title = contentString(block.title);
  if (title !== undefined) content.title = title;

  const url = contentString(block.url);
  if (url !== undefined) content.url = url;

  if (primaryFileRef) content.fileRef = primaryFileRef;
  if (fileRefs) content.fileRefs = fileRefs;

  const fileName =
    contentString(block.fileName) ?? contentString(block.file_name);
  if (fileName !== undefined) content.fileName = fileName;

  const mimeType =
    contentString(block.mimeType) ?? contentString(block.mime_type);
  if (mimeType !== undefined) content.mimeType = mimeType;

  const sizeBytes =
    contentNumber(block.sizeBytes) ?? contentNumber(block.size_bytes);
  if (sizeBytes !== undefined) content.sizeBytes = sizeBytes;

  const entryKind =
    contentString(block.entryKind) ?? contentString(block.entry_kind);
  if (entryKind !== undefined) content.entryKind = entryKind;

  const generationPrompt =
    contentString(block.generationPrompt) ??
    contentString(block.generation_prompt);
  if (generationPrompt !== undefined)
    content.generationPrompt = generationPrompt;

  const quoteRef = normalizeContentQuoteRef(
    'quoteRef' in block
      ? block.quoteRef
      : 'quote_ref' in block
        ? block.quote_ref
        : undefined
  );
  if (quoteRef) content.quoteRef = quoteRef;

  const requestId =
    contentString(block.requestId) ?? contentString(block.request_id);
  if (requestId !== undefined) content.requestId = requestId;

  const requestType =
    contentString(block.requestType) ?? contentString(block.request_type);
  if (requestType !== undefined) content.requestType = requestType;

  const status = contentString(block.status);
  if (status !== undefined) content.status = status;

  const triggerClientInstanceId =
    contentString(block.triggerClientInstanceId) ??
    contentString(block.trigger_client_instance_id);
  if (triggerClientInstanceId !== undefined)
    content.triggerClientInstanceId = triggerClientInstanceId;

  const authorizationURL =
    contentString(block.authorizationURL) ??
    contentString(block.authorization_url);
  if (authorizationURL !== undefined)
    content.authorizationURL = authorizationURL;

  const loginURL =
    contentString(block.loginURL) ?? contentString(block.login_url);
  if (loginURL !== undefined) content.loginURL = loginURL;

  const provider = contentString(block.provider);
  if (provider !== undefined) content.provider = provider;

  const alias = contentString(block.alias);
  if (alias !== undefined) content.alias = alias;

  const reason = contentString(block.reason);
  if (reason !== undefined) content.reason = reason;

  const startURL =
    contentString(block.startURL) ?? contentString(block.start_url);
  if (startURL !== undefined) content.startURL = startURL;

  const description = contentString(block.description);
  if (description !== undefined) content.description = description;

  const memoryId =
    contentString(block.memoryId) ?? contentString(block.memory_id);
  if (memoryId !== undefined) content.memoryId = memoryId;

  const environmentId =
    contentString(block.environmentId) ??
    contentString(block.environment_id) ??
    contentString(block.environment);
  if (environmentId !== undefined) content.environmentId = environmentId;

  const environmentLabel =
    contentString(block.environmentLabel) ??
    contentString(block.environment_label);
  if (environmentLabel !== undefined)
    content.environmentLabel = environmentLabel;

  const siteName =
    contentString(block.siteName) ?? contentString(block.site_name);
  if (siteName !== undefined) content.siteName = siteName;

  const entrypointPath =
    contentString(block.entrypointPath) ?? contentString(block.entrypoint_path);
  if (entrypointPath !== undefined) content.entrypointPath = entrypointPath;

  const verifiedStatus =
    contentString(block.verifiedStatus) ?? contentString(block.verified_status);
  if (verifiedStatus !== undefined) content.verifiedStatus = verifiedStatus;

  const hostAccess = metadataHostAccess(block.hostAccess ?? block.host_access);
  if (hostAccess) content.hostAccess = hostAccess;

  const computerUseStart = metadataComputerUseStart(
    block.computerUseStart ?? block.computer_use_start
  );
  if (computerUseStart) content.computerUseStart = computerUseStart;

  return content;
}

function conversationMessageContent(
  content: unknown
): ConversationMessageContent[] {
  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }
  if (!Array.isArray(content)) return [];
  return content.flatMap(block => {
    const normalized = normalizeConversationContentBlock(block);
    return normalized ? [normalized] : [];
  });
}

function conversationMessageFromWebMessage(
  message: WebAgentConversationMessage
): ConversationMessage {
  const appRequestContent = appRequestContentFromMessageParts({
    kind: message.kind,
    metadata: message.metadata,
  });
  const conversationMessage: ConversationMessage = {
    id: `willow-${message.messageId}`,
    type: 'message',
    role: appRequestContent ? 'system' : conversationMessageRole(message),
    timestamp: message.createdAt,
    content: appRequestContent ?? conversationMessageContent(message.content),
    messageId: message.messageId,
    senderName: normalizeText(message.agentName) ?? undefined,
    senderAvatarURL: normalizeText(message.agentAvatarURL) ?? undefined,
  };
  if (conversationMessage.senderName) {
    conversationMessage.sourceAgentName = conversationMessage.senderName;
  }
  if (message.metadata !== undefined) {
    conversationMessage.metadata = message.metadata;
  }
  return withRuntimeIdentity(
    conversationMessage,
    runtimeIdentityFromConversationMessage(message)
  );
}

function conversationMessageFromDraft(
  draft: WebAgentConversationMessageDraft
): ConversationMessage {
  return withRuntimeIdentity(
    {
      id: `willow-draft-${draft.draftId}`,
      type: 'message',
      role: 'assistant',
      timestamp: draft.updatedAt ?? draft.createdAt ?? Date.now() / 1000,
      content: conversationMessageContent(draft.content),
      senderName: normalizeText(draft.agentName) ?? undefined,
      senderAvatarURL: normalizeText(draft.agentAvatarURL) ?? undefined,
    },
    runtimeIdentityFromConversationMessage(draft)
  );
}

function participantMatchesIdentity(
  participant: WebAgentConversationParticipant,
  identity: ConversationMessageRuntimeIdentity
) {
  if (identity.participantId && participant.participantId) {
    return participant.participantId === identity.participantId;
  }
  if (identity.agentId && identity.sessionId) {
    return (
      normalizeText(participant.agentId) === identity.agentId &&
      normalizeText(participant.sessionId) === identity.sessionId
    );
  }
  if (identity.agentId) {
    return normalizeText(participant.agentId) === identity.agentId;
  }
  return false;
}

function messageParticipantIdentity(
  message: ConversationMessage,
  participants: WebAgentConversationParticipant[]
) {
  const identity = messageRuntimeIdentity(message);
  if (!identity) return null;
  return participants.find(participant =>
    participantMatchesIdentity(participant, identity)
  );
}

function withConversationParticipantIdentity(
  message: ConversationMessage,
  participants: WebAgentConversationParticipant[]
) {
  const participant = messageParticipantIdentity(message, participants);
  if (!participant) return message;

  const senderName =
    normalizeText(participant.agentName) ??
    normalizeText(message.senderName) ??
    normalizeText(message.sourceAgentName);
  const senderAvatarURL =
    normalizeText(participant.agentAvatarURL) ??
    normalizeText(message.senderAvatarURL);
  if (!senderName && !senderAvatarURL) return message;

  return {
    ...message,
    senderName: senderName ?? undefined,
    senderAvatarURL: senderAvatarURL ?? undefined,
    sourceAgentName: senderName ?? undefined,
  };
}

function stripEmptyPlaceholder(text: string) {
  return text.replace(/<empty\s*\/>/g, '').trim();
}

function normalizedContentSignature(message: ConversationMessage) {
  const normalizedFileRef = (
    ref: NonNullable<ConversationMessageContent['fileRef']>
  ) => ({
    refId: ref.refId?.trim() ?? '',
    kind: ref.kind?.trim() ?? '',
    source: ref.source?.trim() ?? '',
    path: ref.path?.trim() ?? '',
    displayURL: ref.displayURL?.trim() ?? '',
    downloadURL: ref.downloadURL?.trim() ?? '',
    thumbnailURL: ref.thumbnailURL?.trim() ?? '',
    publicShareURL: ref.publicShareURL?.trim() ?? '',
    authRequired: ref.authRequired ?? null,
    authScope: ref.authScope?.trim() ?? '',
    name: ref.name?.trim() ?? '',
    mimeType: ref.mimeType?.trim() ?? '',
    sizeBytes: ref.sizeBytes ?? null,
    contentHash: ref.contentHash?.trim() ?? '',
    environmentId: ref.environmentId?.trim() ?? '',
    sourceSessionId: ref.sourceSessionId?.trim() ?? '',
    agentId: ref.agentId?.trim() ?? '',
    signature: ref.signature?.trim() ?? '',
    expiresAt: ref.expiresAt ?? null,
  });
  const hasFileRefIdentity = (
    ref: ReturnType<typeof normalizedFileRef>
  ): boolean =>
    !!(
      ref.refId ||
      ref.path ||
      ref.displayURL ||
      ref.downloadURL ||
      ref.thumbnailURL ||
      ref.publicShareURL ||
      ref.contentHash ||
      ref.signature
    );

  const normalized = (message.content ?? []).flatMap<
    | {
        type: string;
        text: string;
      }
    | {
        type: string;
        path: string;
        url: string;
        fileName: string;
        mimeType: string;
        fileRef: ReturnType<typeof normalizedFileRef> | null;
        fileRefs: Array<ReturnType<typeof normalizedFileRef>>;
      }
  >(content => {
    if (content.type === 'text') {
      const text = stripEmptyPlaceholder(content.text ?? '');
      return text ? [{ type: 'text', text }] : [];
    }

    const path = content.fileRef?.path?.trim() ?? '';
    const url = content.url?.trim() ?? '';
    const fileName = content.fileName?.trim() ?? '';
    const mimeType = content.mimeType?.trim() ?? '';
    const fileRef = content.fileRef ? normalizedFileRef(content.fileRef) : null;
    const fileRefs = (content.fileRefs ?? [])
      .map(normalizedFileRef)
      .filter(hasFileRefIdentity);
    if (!path && !url && !fileName && !fileRef && fileRefs.length === 0) {
      return [];
    }
    return [
      {
        type: content.type,
        path,
        url,
        fileName,
        mimeType,
        fileRef,
        fileRefs,
      },
    ];
  });
  return JSON.stringify(normalized);
}

function matchesPendingLocalUserMessage(
  pendingMessage: ConversationMessage,
  persistedMessage: ConversationMessage
) {
  return (
    pendingMessage.role === 'user' &&
    persistedMessage.role === 'user' &&
    pendingMessage.id.startsWith('local-') &&
    persistedMessage.id.startsWith('willow-') &&
    messageClientRequestId(pendingMessage) !== undefined &&
    messageClientRequestId(pendingMessage) ===
      messageClientRequestId(persistedMessage)
  );
}

function isAssistantDraftMessage(message: ConversationMessage) {
  return (
    message.type === 'message' &&
    message.role === 'assistant' &&
    message.id.startsWith('willow-draft-')
  );
}

function isPersistedAssistantMessage(message: ConversationMessage) {
  return (
    message.type === 'message' &&
    message.role === 'assistant' &&
    message.id.startsWith('willow-') &&
    !message.id.startsWith('willow-draft-')
  );
}

function matchesPendingAssistantDraft(
  pendingMessage: ConversationMessage,
  persistedMessage: ConversationMessage
) {
  const pendingIdentity = messageRuntimeIdentity(pendingMessage);
  const persistedIdentity = messageRuntimeIdentity(persistedMessage);
  return (
    isAssistantDraftMessage(pendingMessage) &&
    isPersistedAssistantMessage(persistedMessage) &&
    !!pendingIdentity?.participantId &&
    pendingIdentity.participantId === persistedIdentity?.participantId &&
    normalizedContentSignature(pendingMessage) ===
      normalizedContentSignature(persistedMessage) &&
    Math.abs(pendingMessage.timestamp - persistedMessage.timestamp) < 600
  );
}

function isStaleAssistantDraft(
  pendingMessage: ConversationMessage,
  persistedMessage: ConversationMessage
) {
  return (
    matchesPendingAssistantDraft(pendingMessage, persistedMessage) &&
    persistedMessage.timestamp >= pendingMessage.timestamp
  );
}

function appendConversationMessage(
  current: ConversationMessage[],
  nextMessage: ConversationMessage
): ConversationMessage[] {
  const existingIndex = current.findIndex(
    message => message.id === nextMessage.id
  );
  if (existingIndex !== -1) {
    const next = [...current];
    next[existingIndex] = nextMessage;
    return next;
  }

  if (nextMessage.role === 'user' && nextMessage.id.startsWith('willow-')) {
    const index = current.findIndex(message =>
      matchesPendingLocalUserMessage(message, nextMessage)
    );
    if (index !== -1) {
      const next = [...current];
      next[index] = nextMessage;
      return next;
    }
  }

  if (isPersistedAssistantMessage(nextMessage)) {
    const index = current.findLastIndex(message =>
      matchesPendingAssistantDraft(message, nextMessage)
    );
    if (index !== -1) {
      const next = [...current];
      next[index] = nextMessage;
      return next;
    }
  }

  return [...current, nextMessage];
}

function applyDraftToConversationMessages(
  current: ConversationMessage[],
  draft: WebAgentConversationMessageDraft
) {
  const draftMessageId = `willow-draft-${draft.draftId}`;

  if (draft.status === 'cancelled') {
    return current.filter(message => message.id !== draftMessageId);
  }
  if (draft.status === 'completed') {
    return current;
  }

  const nextMessage = conversationMessageFromDraft(draft);
  if (current.some(message => isStaleAssistantDraft(nextMessage, message))) {
    return current.filter(message => message.id !== draftMessageId);
  }

  const existingIndex = current.findIndex(
    message => message.id === draftMessageId
  );
  if (existingIndex === -1) {
    return [...current, nextMessage];
  }

  const messages = [...current];
  messages[existingIndex] = nextMessage;
  return messages;
}

function isConversationMessage(
  value: WebAgentConversationMessage | ConversationMessage
): value is ConversationMessage {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.timestamp === 'number'
  );
}

function conversationMessagesFromRawMessages(
  messages: Array<WebAgentConversationMessage | ConversationMessage>
) {
  return messages.map(message =>
    isConversationMessage(message)
      ? message
      : conversationMessageFromWebMessage(message)
  );
}

type ConversationMessageThreadLoader = (
  conversationId: string
) => Promise<Array<WebAgentConversationMessage | ConversationMessage>>;

export class ConversationMessageThread {
  readonly localMessages = signal<ConversationMessage[]>([]);
  readonly localRenderCache = signal<
    Record<string, Record<string, ConversationMessageAttachmentImageSize>>
  >({});
  private readonly localSnapshotHydrated = signal(false);
  private readonly localSnapshotStale = signal(false);
  private readonly localSnapshotRefreshing = signal(false);
  private loadPromise: Promise<ConversationMessage[]> | null = null;

  readonly messages = computed(() => {
    const rawMessages =
      this.conversationData?.messageSnapshotFor(this.conversationId) ?? [];
    let nextMessages = conversationMessagesFromRawMessages(rawMessages);
    for (const message of this.localMessages.value) {
      nextMessages = appendConversationMessage(nextMessages, message);
    }
    for (const message of this.conversationData?.createdMessagesFor(
      this.conversationId
    ) ?? []) {
      nextMessages = appendConversationMessage(
        nextMessages,
        conversationMessageFromWebMessage(message)
      );
    }
    for (const draft of this.conversationData?.draftsFor(this.conversationId) ??
      []) {
      nextMessages = applyDraftToConversationMessages(nextMessages, draft);
    }
    for (const [messageId, attachments] of Object.entries(
      this.localRenderCache.value
    )) {
      for (const [attachmentKey, imageSize] of Object.entries(attachments)) {
        nextMessages = updateConversationMessagesAttachmentImageSize(
          nextMessages,
          messageId,
          attachmentKey,
          imageSize
        );
      }
    }
    const participants =
      this.conversationData?.conversationInfoFor(this.conversationId)
        ?.participants ?? [];
    if (participants.length > 0) {
      nextMessages = nextMessages.map(message =>
        withConversationParticipantIdentity(message, participants)
      );
    }
    return nextMessages;
  });
  readonly snapshotHydrated = computed(() => this.localSnapshotHydrated.value);
  readonly snapshotStale = computed(() => this.localSnapshotStale.value);
  readonly snapshotRefreshing = computed(
    () => this.localSnapshotRefreshing.value
  );
  readonly visibleLoading = computed(
    () => this.snapshotRefreshing.value && this.messages.value.length === 0
  );
  readonly needsSnapshotRefresh = computed(
    () => !this.snapshotHydrated.value || this.snapshotStale.value
  );

  constructor(
    readonly conversationId: string,
    private readonly conversationData?: ConversationLiveDataset
  ) {}

  markSnapshotHydrated() {
    this.localSnapshotHydrated.value = true;
    this.localSnapshotStale.value = false;
    this.localSnapshotRefreshing.value = false;
  }

  async ensureSnapshotHydrated(
    loadMessages: ConversationMessageThreadLoader,
    options: { force?: boolean } = {}
  ) {
    if (!options.force && !this.needsSnapshotRefresh.value) {
      return this.messages.value;
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.startRefresh();
    const loadPromise = (async () => {
      await loadMessages(this.conversationId);
      this.markSnapshotHydrated();
      return this.messages.value;
    })();
    this.loadPromise = loadPromise;

    try {
      return await loadPromise;
    } catch (error) {
      this.finishRefreshAfterError();
      throw error;
    } finally {
      if (this.loadPromise === loadPromise) {
        this.loadPromise = null;
      }
    }
  }

  appendLocalUserMessage(
    id: string,
    timestamp: number,
    content: ConversationMessageContent[],
    metadata?: unknown
  ) {
    const localMessage: ConversationMessage = {
      id: `local-${id}`,
      type: 'message',
      role: 'user',
      timestamp,
      content,
    };
    if (metadata !== undefined) {
      localMessage.metadata = metadata;
    }
    this.localMessages.value = appendConversationMessage(
      this.localMessages.value,
      localMessage
    );
    return localMessage;
  }

  confirmLocalUserMessage(localMessageId: string, messageId: string) {
    const persistedMessageId = `willow-${messageId}`;
    this.localMessages.value = this.localMessages.value.map(message =>
      message.id === localMessageId
        ? {
            ...message,
            id: persistedMessageId,
          }
        : message
    );
  }

  remove(messageId: string) {
    this.localMessages.value = this.localMessages.value.filter(
      message => message.id !== messageId
    );
  }

  recordAttachmentImageLayout(
    messageId: string,
    attachmentKey: string,
    imageSize: ConversationMessageAttachmentImageSize
  ) {
    const normalizedMessageId = normalizeText(messageId);
    const normalizedAttachmentKey = normalizeText(attachmentKey);
    if (
      !normalizedMessageId ||
      !normalizedAttachmentKey ||
      !Number.isFinite(imageSize.width) ||
      !Number.isFinite(imageSize.height) ||
      imageSize.width <= 0 ||
      imageSize.height <= 0
    ) {
      return;
    }

    const existing =
      this.localRenderCache.value[normalizedMessageId]?.[
        normalizedAttachmentKey
      ];
    if (
      existing?.width === imageSize.width &&
      existing.height === imageSize.height
    ) {
      return;
    }

    this.localRenderCache.value = {
      ...this.localRenderCache.value,
      [normalizedMessageId]: {
        ...this.localRenderCache.value[normalizedMessageId],
        [normalizedAttachmentKey]: imageSize,
      },
    };
  }

  startRefresh() {
    this.localSnapshotRefreshing.value = true;
  }

  finishRefreshAfterError() {
    this.localSnapshotRefreshing.value = false;
  }

  markSnapshotStale() {
    if (this.snapshotHydrated.value) {
      this.localSnapshotStale.value = true;
    }
  }

  localContentFromOutbound(
    content: string | WebAgentMessageContent[],
    localContent?: ConversationMessageContent[]
  ) {
    if (localContent) return localContent;
    if (typeof content === 'string') {
      return content.trim().length > 0 ? [{ type: 'text', text: content }] : [];
    }
    return conversationMessageContent(content);
  }

  reset() {
    this.localMessages.value = [];
    this.localSnapshotHydrated.value = false;
    this.localSnapshotStale.value = false;
    this.localSnapshotRefreshing.value = false;
    this.loadPromise = null;
  }
}
